/**
 * Polyfills required by pdfjs-dist v6 on older Safari.
 *
 * pdfjs-dist ships no polyfills of its own (they were dropped in v4.3), and CRA
 * does not transpile node_modules, so these built-ins reach the browser as-is:
 *
 *   Promise.withResolvers()              Chrome 119 / Safari 17.4
 *   URL.parse()                          Chrome 126 / Safari 18.0
 *   ReadableStream[Symbol.asyncIterator] Chrome 124 / Safari: unimplemented
 *
 * The first two make getDocument() reject with "... is not a function". The
 * third breaks PDFPageProxy.getTextContent(), which does `for await (const
 * chunk of this.streamTextContent(...))` over a ReadableStream — WebKit has
 * never shipped async iteration on streams (bug 194379), so text extraction
 * threw "Object is not async iterable" while page rendering was unaffected.
 *
 * Keep this in sync with scripts/patch-pdf-worker.js, which injects the same
 * shims into the worker — the worker is a separate realm and does not inherit
 * these.
 */

type Resolvers<T> = {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: any) => void;
};

if (typeof (Promise as any).withResolvers !== "function") {
  (Promise as any).withResolvers = function withResolvers<T>(): Resolvers<T> {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: any) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

if (typeof (URL as any).parse !== "function") {
  (URL as any).parse = function parse(
    url: string | URL,
    base?: string | URL
  ): URL | null {
    try {
      return base === undefined ? new URL(url) : new URL(url, base);
    } catch {
      return null;
    }
  };
}

// Mirrors the spec's ReadableStream.prototype.values(), which is what
// `for await (... of stream)` desugars to.
if (
  typeof ReadableStream !== "undefined" &&
  !(ReadableStream.prototype as any)[Symbol.asyncIterator]
) {
  const values = function (
    this: ReadableStream,
    { preventCancel = false }: { preventCancel?: boolean } = {}
  ) {
    const reader = this.getReader();
    return {
      next() {
        return reader.read().then(
          (result) => {
            if (result.done) {
              reader.releaseLock();
            }
            return result;
          },
          (reason) => {
            reader.releaseLock();
            throw reason;
          }
        );
      },
      return(value?: any) {
        if (preventCancel) {
          reader.releaseLock();
          return Promise.resolve({ done: true, value });
        }
        const cancelled = reader.cancel(value);
        reader.releaseLock();
        return cancelled.then(() => ({ done: true, value }));
      },
      [Symbol.asyncIterator]() {
        return this;
      },
    };
  };

  (ReadableStream.prototype as any).values = values;
  (ReadableStream.prototype as any)[Symbol.asyncIterator] = values;
}

export {};
